import { Component, OnInit, Input, OnDestroy, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { StoreService } from 'core/services';
import { Subscription } from 'rxjs';
import { ListOptions } from '../list-options';
import { CheckboxQuestion } from 'core/components/forms';

@Component({
  selector: 'lms-list-row-selector',
  templateUrl: './list-row-selector.component.html',
  styleUrls: ['./list-row-selector.component.scss']
})
export class ListRowSelectorComponent implements OnInit, OnDestroy, OnChanges {
  @Input() listId: string;
  @Input() rowId: string;
  @Input() isMultiple: boolean;
  @Input() selectedRow?: boolean;
  @Output() selectionChanged: EventEmitter<any[]> = new EventEmitter<any[]>();

  public checkboxQuestion: CheckboxQuestion;
  private selectionKey: string;
  private documentsKey: string;
  private isSelected = false;
  private selection: any;
  private listElements: any[];
  private listOptions: ListOptions;
  private listSubscription: Subscription;
  private docsSubscription: Subscription;
  private selectionSubscription: Subscription;

  constructor() {}

  public ngOnInit(): void {
    this.selectionKey = `${this.listId}_selection`;
    this.documentsKey = `${this.listId}_DOCS`;

    this.initializeSubscriptions();
    this.setQuestion();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes &&
      changes['selectedRow'] &&
      !!changes['selectedRow']['previousValue'] === changes['selectedRow']['previousValue']
    ) {
      this.checkboxQuestion.value = changes['selectedRow']['currentValue'];
    }
  }

  public ngOnDestroy(): void {
    if (this.isMultiple) {
      this.docsSubscription.unsubscribe();
    }
    this.listSubscription.unsubscribe();
    this.selectionSubscription.unsubscribe();
  }

  /**
   * Create and subscribe on the events (selection) in the rows of a table
   * @private
   */
  private initializeSubscriptions(): void {
    this.listSubscription = StoreService.get(this.listId).subscribe((data) => {
      this.listOptions = data || {};
    });

    if (this.isMultiple) {
      this.docsSubscription = StoreService.get(this.documentsKey).subscribe((elements) => {
        this.listElements = elements || [];
        this.isSelected = this.listOptions.selected;
        const selected = this.isSelected ? new Set(this.listElements.map((doc) => doc.id)) : new Set();
        StoreService.create(this.selectionKey, { selected, isMultiple: true });
        this.emitSelectionChanged(selected);
      });
    }

    this.selectionSubscription = StoreService.get(this.selectionKey).subscribe((selection) => {
      this.selection = selection;
      if (selection) {
        if (!this.isMultiple) {
          if (selection.isMultiple) {
            this.isSelected = selection.selected.has(this.rowId);
          }
        } else if (!selection.isMultiple) {
          this.isSelected = false;
          this.emitSelectionChanged(this.selection.selected);
        }
      }
    });
  }

  /**
   * Creation of the checkbox question
   * @private
   */
  private setQuestion(): void {
    this.checkboxQuestion = new CheckboxQuestion({
      key: this.listId,
      type: 'checkbox',
      value: this.isSelected,
      eventHandlers: {
        onChange: (isChecked) => {
          this.isSelected = isChecked;
          if (this.isMultiple && (this.selectedRow || isChecked)) {
            this.onChangeMultiple();
          } else {
            this.onChangeSingle();
          }
        }
      }
    });
  }

  /**
   * When checkbox is used as a multiple checkbox of a list
   * @private
   */
  private onChangeMultiple(): void {
    const selected = this.isSelected ? new Set(this.listElements.map((doc) => doc.id)) : new Set();
    StoreService.update(this.selectionKey, { selected, isMultiple: true });
    this.emitSelectionChanged(selected);
  }

  /**
   * When checkbox is used as a single checkbox
   * @private
   */
  private onChangeSingle(): void {
    const selected = this.selection ? this.selection.selected : new Set();
    if (this.isSelected) {
      selected.add(this.rowId);
    } else {
      selected.delete(this.rowId);
    }
    StoreService.update(this.selectionKey, { selected, isMultiple: false });
  }

  /**
   * Emit a selection of rows
   * @param selected
   * @private
   */
  private emitSelectionChanged(selected?: Set<any>): void {
    const selectedDocs = selected ? this.listElements.filter((element) => selected.has(element.id)) : [];
    this.selectionChanged.emit(selectedDocs);
  }
}
